import { useTheme } from '@mui/material'
import ReactEcharts, { EChartsOption } from 'echarts-for-react'
import React, { useMemo } from 'react'

export interface StackedBarData {
  name: string
  count: number
  color: string
}

interface StackedBarChartProps {
  data: StackedBarData[]
  height?: number
  onBarClick?: (dataItem: StackedBarData) => void
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({ data, height = 32, onBarClick }) => {
  const { palette } = useTheme()

  const options: EChartsOption = useMemo(() => {
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: { seriesName: string; value: number }) => {
          const { seriesName, value } = params
          return `${seriesName}: ${value}`
        },
      },
      legend: {
        show: false,
      },
      grid: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        show: false,
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
      },
      series: data.map((bar, index) => {
        return {
          name: bar.name,
          showBackground: true,
          backgroundStyle: {
            color: palette.grey[100],
          },
          type: 'bar',
          stack: 'total',
          data: [bar.count],
          itemStyle: {
            color: bar.color,
            borderType: 'solid',
            borderWidth: 1,
            borderColor: 'white',
          },
          emphasis: {
            focus: 'self',
          },
        }
      }),
    }
  }, [data, palette])

  const events = useMemo(() => {
    if (!onBarClick) return undefined

    return {
      click: (params: { seriesName: string; seriesIndex: number }) => {
        if (params.seriesName && params.seriesIndex !== undefined) {
          const clickedData = data[params.seriesIndex]
          if (clickedData) {
            onBarClick(clickedData)
          }
        }
      },
    }
  }, [onBarClick, data])

  return <ReactEcharts option={options} onEvents={events} style={{ height, zIndex: 1, width: '100%' }} />
}

export default StackedBarChart
